import { Atom, useAtomValue } from 'jotai';
import { PitchEvents } from '@/types/visualisation';
import { useVideoDataForEvents } from './useVideoDataForEvents';

/**
 * This hook takes an event markers atom, and determines for each event whether video is available or not.
 * First of all it filters based on the video status field of the events.
 * It then uses a hook exposed by the video client to check whether the video is available for the game and period of each event.
 * Once it has determined which videos are available, it returns the video IDs of the remaining available videos, the count of unavailable
 * videos, and an isLoading boolean to indicate whether the hook has finished checking the availability of the videos.
 *
 * NOTE - this hook also returns a ref. It's important that this ref is assigned to a component that is visible on the page.
 * If the ref is ignored, or given to a component that is not visible, the video client will not perform the check and the
 * isLoading state will permanently remain true.
 * */
export const useVideoDataFromEventMarkers = <T extends Element>(eventMarkersAtom: Atom<Promise<PitchEvents>>) => {
  const eventsForMetric = useAtomValue(eventMarkersAtom);
  return useVideoDataForEvents<T>([...eventsForMetric.arrowEvents, ...eventsForMetric.markerEvents]);
};
